import { Check } from 'lucide-react';
import { BOT_PERMISSION_ORDER, hasPermission, Permission, PERMISSION_INFO } from '@spawnwire/shared';
import { clsx } from '../../components/ui';

/** Bot permission names for a set of bits, in the usual order. */
export function botPermissionLabels(bits: number): string[] {
  return BOT_PERMISSION_ORDER.filter((p) => hasPermission(bits, Permission[p])).map((p) => PERMISSION_INFO[p].label);
}

/**
 * The permissions a bot can have, as checkboxes. `allowed` greys out what can't be ticked (on the consent
 * screen: what the person adding it doesn't have), with `disabledHint` saying why.
 */
export function BotPermissionChecklist({
  value,
  onChange,
  only,
  allowed,
  disabledHint = "You don't have this permission",
}: {
  value: number;
  onChange: (bits: number) => void;
  /** Only list these (the consent screen lists what the bot asked for). */
  only?: number;
  allowed?: number;
  disabledHint?: string;
}) {
  const names = BOT_PERMISSION_ORDER.filter((p) => only === undefined || hasPermission(only, Permission[p]));
  if (names.length === 0) return <p className="text-sm text-muted">No permissions.</p>;
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
      {names.map((p) => {
        const bit = Permission[p];
        const can = allowed === undefined || hasPermission(allowed, bit);
        const on = can && hasPermission(value, bit);
        return (
          <li key={p}>
            <label className={clsx('flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-hover', !can && 'cursor-not-allowed opacity-50 hover:bg-transparent')}>
              <input type="checkbox" className="peer sr-only" checked={on} disabled={!can} onChange={() => onChange(on ? value & ~bit : value | bit)} />
              <span
                className={clsx(
                  'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border-2 transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent',
                  on ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong',
                )}
                aria-hidden
              >
                {on && <Check className="size-3.5" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-fg">{PERMISSION_INFO[p].label}</span>
                <span className="block text-xs text-muted">{can ? PERMISSION_INFO[p].description : disabledHint}</span>
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
